import { useState, useEffect, type FormEvent, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../api/client.js'
import { useAuthStore, type User } from '../store/authStore.js'
import { getBuildInfo } from '../utils/buildInfo.js'
import { deriveTenantSlugFromHost } from '../utils/deriveTenantSlugFromHost.js'
import voz360Logo from '../assets/voz360.png'
import LoginMfaDialog from '../components/auth/LoginMfaDialog.js'
import ForcedPasswordChangeDialog from '../components/auth/ForcedPasswordChangeDialog.js'

interface LoginApiResponse {
  requiresMfa?: boolean
  setupRequired?: boolean
  secret?: string
  otpauthUrl?: string
  preAuthToken?: string
  mustChangePassword?: boolean
  passwordChangeToken?: string
  user?: Omit<User, 'sub'> & { id: string }
  csrfToken?: string
}

interface TenantConfig {
  enableGoogleSignIn?: boolean
  googleClientId?: string
  enableMicrosoftSignIn?: boolean
  microsoftClientId?: string
}

interface AuthMeResponse {
  user: User
  csrfToken: string
}

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  user_not_found: 'No account is associated with this sign-in account. Please contact your administrator.',
  oauth_failed: 'Sign-in failed. Please try again or use your login ID.',
  already_logged_in: 'This user is already logged in on another session. Log out there first.',
}

export default function Login(): JSX.Element {
  const [step, setStep] = useState<'loginId' | 'password'>('loginId')
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [googleSignInEnabled, setGoogleSignInEnabled] = useState(false)
  const [_microsoftSignInEnabled, setMicrosoftSignInEnabled] = useState(false)
  const [mfaOpen, setMfaOpen] = useState(false)
  const [preAuthToken, setPreAuthToken] = useState<string | null>(null)
  const [passwordChangeToken, setPasswordChangeToken] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [mfaSetup, setMfaSetup] = useState<{ secret: string; otpauthUrl: string } | null>(null)
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const oauthError = params.get('error')
    if (oauthError && OAUTH_ERROR_MESSAGES[oauthError]) {
      setError(OAUTH_ERROR_MESSAGES[oauthError])
    }
    // Signed out because this account signed in on another device.
    try {
      if (sessionStorage.getItem('kc3.logoutReason') === 'replaced') {
        sessionStorage.removeItem('kc3.logoutReason')
        setError('You were signed out because this account signed in on another device or browser.')
      }
    } catch { /* private mode */ }

    if (params.get('google_auth') === 'success' || params.get('microsoft_auth') === 'success') {
      const oauthProvider = params.get('google_auth') === 'success' ? 'Google' : 'Microsoft'
      const hydrateOAuthSession = async () => {
        setLoading(true)
        try {
          const resp = await api.get<AuthMeResponse>('/auth/me')
          if (resp?.user && resp?.csrfToken) {
            setSession({ user: { ...resp.user, portal: 'agent' }, csrfToken: resp.csrfToken })
            params.delete('google_auth')
            params.delete('microsoft_auth')
            const query = params.toString()
            window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
            navigate('/')
          }
        } catch (err) {
          console.error('Failed to hydrate OAuth session:', err)
          setError(`${oauthProvider} sign-in succeeded but we could not load your session. Please try again.`)
        } finally {
          setLoading(false)
        }
      }
      hydrateOAuthSession()
      return
    }

    const fetchTenantConfig = async () => {
      try {
        const tenantSlug = deriveTenantSlugFromHost(window.location.host)
        const response = await api.get<TenantConfig>(`/auth/tenant-config?tenantSlug=${tenantSlug}`)
        setGoogleSignInEnabled(response.enableGoogleSignIn ?? false)
        setMicrosoftSignInEnabled(response.enableMicrosoftSignIn ?? false)
      } catch (err) {
        console.error('Failed to fetch tenant config:', err)
      }
    }
    fetchTenantConfig()
  }, [])

  const handleLoginIdSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setError(null)
    
    if (!loginId.trim()) {
      setError('Please enter your login ID')
      return
    }
    
    setLoading(true)
    
    try {
      const tenantSlug = deriveTenantSlugFromHost(window.location.host)
      const response = await api.post<{ exists: boolean }>('/auth/verify-login-id', {
        loginId: loginId.trim(),
        tenantSlug
      })
      
      if (response?.exists) {
        setStep('password')
      } else {
        setError('User not found. Please check your login ID and try again.')
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('User not found. Please check your login ID and try again.')
      } else {
        const errorMessage = err.response?.data?.message || err.message || 'User not found. Please check your login ID.'
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const tenantSlug = deriveTenantSlugFromHost(window.location.host)
      const resp = await api.post<LoginApiResponse>('/auth/login', { 
        loginId, 
        password, 
        tenantSlug 
      })

      if (resp.mustChangePassword && resp.passwordChangeToken) {
        setPasswordChangeToken(resp.passwordChangeToken)
        return
      }

      if (resp.requiresMfa) {
        if (!resp.preAuthToken) {
          setError('Missing MFA challenge token')
          return
        }
        setPreAuthToken(resp.preAuthToken)
        if (resp.setupRequired && resp.secret && resp.otpauthUrl) {
          setMfaSetup({ secret: resp.secret, otpauthUrl: resp.otpauthUrl })
        } else {
          setMfaSetup(null)
        }
        setMfaOpen(true)
        return
      }

      if (resp.user && resp.csrfToken) {
        const user: User = { ...resp.user, sub: resp.user.id, portal: 'agent' }
        setSession({ user, csrfToken: resp.csrfToken })
        navigate('/')
        return
      }

      setError('Unexpected response from server')
    } catch (err: any) {
      const serverMessage = err.body?.message ?? err.message
      const serverError = err.body?.error
      if (serverError === 'ACCOUNT_DISABLED') {
        setError(serverMessage || 'Your account is inactive. Contact your administrator.')
      } else if (serverError === 'ACCOUNT_LOCKED') {
        setError(serverMessage || 'Account is locked. Contact your administrator.')
      } else if (serverError === 'ALREADY_LOGGED_IN') {
        setError(serverMessage || 'This account is already signed in on another device. Sign out there first.')
      } else if (serverError === 'TOO_MANY_FAILED_LOGINS') {
        setError('Too many failed login attempts. Please try again later.')
      } else if (serverMessage && serverMessage !== 'POST /auth/login → 401') {
        setError(serverMessage)
      } else {
        setError('Invalid credentials')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async (): Promise<void> => {
    setError(null)
    setLoading(true)
    try {
      const tenantSlug = deriveTenantSlugFromHost(window.location.host)
      window.location.href = `/api/auth/google?tenantSlug=${tenantSlug}`
    } catch (err) {
      setError((err as Error).message || 'Failed to initiate Google Sign-In')
      setLoading(false)
    }
  }

  const handleMicrosoftSignIn = async (): Promise<void> => {
    setError(null)
    setLoading(true)
    try {
      const tenantSlug = deriveTenantSlugFromHost(window.location.host)
      window.location.href = `/api/auth/microsoft?tenantSlug=${tenantSlug}`
    } catch (err) {
      setError((err as Error).message || 'Failed to initiate Microsoft Sign-In')
      setLoading(false)
    }
  }

  const handleBack = (): void => {
    setStep('loginId')
    setPassword('')
    setError(null)
  }

  return (
    <div className="min-h-screen flex">
      {passwordChangeToken && (
        <ForcedPasswordChangeDialog
          passwordChangeToken={passwordChangeToken}
          currentPassword={password}
          onClose={() => setPasswordChangeToken(null)}
          onDone={() => {
            setPasswordChangeToken(null)
            setPassword('')
            setNotice('Password changed. Sign in with your new password.')
          }}
        />
      )}
      {mfaOpen && preAuthToken && (
        <LoginMfaDialog
          preAuthToken={preAuthToken}
          setup={mfaSetup}
          onClose={() => setMfaOpen(false)}
        />
      )}
      {/* Left Side — Clean, Modern, Elegant Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0B1120] relative overflow-hidden">
        {/* Soft ambient glows — brand colors, very subtle */}
        <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-[#3366FF] opacity-[0.07] rounded-full blur-[140px]" />
        <div className="absolute bottom-[-15%] right-[-10%] w-[500px] h-[500px] bg-[#FF9933] opacity-[0.05] rounded-full blur-[120px]" />
        <div className="absolute top-[40%] right-[20%] w-[300px] h-[300px] bg-[#3366FF] opacity-[0.03] rounded-full blur-[100px]" />

        {/* Single elegant concentric ring */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[440px] h-[440px] rounded-full border border-white/[0.04]"
          style={{ animation: 'gentlePulse 8s ease-in-out infinite' }}
        />
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[340px] h-[340px] rounded-full border border-white/[0.025]"
          style={{ animation: 'gentlePulse 8s ease-in-out 2.5s infinite' }}
        />

        {/* Very subtle dot grid */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />

        {/* One floating accent shape — minimal */}
        <div
          className="absolute top-[18%] right-[22%] w-8 h-8 border border-[#FF9933]/15 rounded-md"
          style={{ animation: 'gentleFloat 12s ease-in-out infinite' }}
        />
        <div
          className="absolute bottom-[25%] left-[15%] w-3 h-3 bg-[#3366FF]/20 rounded-full"
          style={{ animation: 'gentleFloat 10s ease-in-out 3s infinite' }}
        />

        <style>{`
          @keyframes gentlePulse {
            0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
            50% { transform: translate(-50%, -50%) scale(1.015); opacity: 0.5; }
          }
          @keyframes gentleFloat {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-14px); }
          }
          @keyframes barGrow {
            from { transform: scaleY(0); }
            to { transform: scaleY(1); }
          }
        `}</style>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Top: Brand */}
          <div>
            <div className="inline-flex items-center gap-3 mb-10">
              <img src={voz360Logo} alt="Voz360" className="h-10" />
              <div className="h-6 w-px bg-white/10" />
              <span className="text-[13px] font-medium text-white/40 tracking-[0.15em] uppercase">Agent Portal</span>
            </div>

            <div className="mb-5">
              <h1 className="text-5xl font-bold leading-[1.1] tracking-tight text-white">
                Agent Workspace
              </h1>
              <div className="mt-3 flex items-center gap-2">
                <div className="h-[2px] w-8 bg-gradient-to-r from-[#3366FF] to-[#FF9933] rounded-full" />
                <span className="text-[11px] font-medium text-white/30 tracking-[0.2em] uppercase">Contact Center Platform</span>
              </div>
            </div>
            <p className="text-base text-white/45 leading-relaxed max-w-sm font-light">
              Everything you need to manage calls, conversations, and customer conversations in one place.
            </p>

            {/* Decorative bridge */}
            <div className="mt-8 flex items-center gap-3">
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              <div className="flex items-center gap-1">
                <div className="w-1 h-1 rounded-full bg-[#3366FF]/50" />
                <div className="w-1 h-1 rounded-full bg-white/20" />
                <div className="w-1 h-1 rounded-full bg-[#FF9933]/50" />
              </div>
              <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            </div>
          </div>

          {/* Middle: Single elegant feature card */}
          <div className="max-w-sm">
            <div className="relative bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 backdrop-blur-md overflow-hidden">
              {/* Subtle top gradient accent */}
              <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#3366FF]/30 to-transparent" />
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[#3366FF]/10 border border-[#3366FF]/15 flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#3366FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.04 12.04 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white/90">Omnichannel Inbox</p>
                  <p className="text-xs text-white/35">Voice, chat, email & WhatsApp</p>
                </div>
              </div>

              {/* Mini activity list */}
              <div className="space-y-3">
                {[
                  { icon: 'call', text: 'Incoming call — Sales Queue', time: 'now', color: 'emerald' },
                  { icon: 'ticket', text: 'Conversation #4921 — Refund request', time: '2m', color: 'blue' },
                  { icon: 'chat', text: 'WhatsApp — Support Queue', time: '5m', color: 'orange' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 group">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      item.color === 'emerald' ? 'bg-emerald-500/10' : item.color === 'blue' ? 'bg-[#3366FF]/10' : 'bg-[#FF9933]/10'
                    }`}>
                      {item.icon === 'call' && (
                        <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      )}
                      {item.icon === 'ticket' && (
                        <svg className="w-3.5 h-3.5 text-[#3366FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                      {item.icon === 'chat' && (
                        <svg className="w-3.5 h-3.5 text-[#FF9933]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-white/75 truncate">{item.text}</p>
                    </div>
                    <span className="text-[11px] text-white/25">{item.time}</span>
                  </div>
                ))}
              </div>

              {/* Mini queue chart */}
              <div className="mt-5 pt-4 border-t border-white/[0.06]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] text-white/35 uppercase tracking-wider">Queue Load</span>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                    <span className="text-[10px] text-emerald-400/80 font-medium">Healthy</span>
                  </div>
                </div>
                <div className="flex items-end gap-[3px] h-7">
                  {[35, 55, 40, 70, 50, 85, 60, 45, 75, 55, 80, 50].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-[1px] bg-gradient-to-t from-[#3366FF]/50 to-[#3366FF]/10"
                      style={{ height: `${h}%`, animation: `barGrow 1.2s ease-out ${i * 0.06}s both` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom: Trust bar */}
          <div className="flex items-center gap-6">
            {[
              { value: '99.9%', label: 'Uptime' },
              { value: '<1s', label: 'Response' },
              { value: '24/7', label: 'Support' },
            ].map((stat, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-1 h-1 rounded-full bg-[#3366FF]/60" />
                <span className="text-xs text-white/30">
                  <span className="text-white/60 font-semibold">{stat.value}</span> {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <img src={voz360Logo} alt="Voz360" className="h-10 mx-auto" />
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Sign In</h2>
            <p className="text-gray-500">Enter your credentials to continue</p>
          </div>

          {notice && !error && (
            <div role="status" className="mb-6 p-4 bg-green-50 border-l-[3px] border-green-600 rounded-[3px]">
              <p className="text-sm text-green-800 font-medium">{notice}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-[3px] border-[#FF9933] rounded-[3px]">
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}

          {step === 'loginId' ? (
            <form onSubmit={handleLoginIdSubmit} className="space-y-5">
              <div>
                <label htmlFor="loginId" className="block text-sm font-medium text-gray-700 mb-2">
                  Login ID
                </label>
                <input
                  id="loginId"
                  type="text"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-[3px] focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:border-[#3366FF] transition-all text-gray-900 placeholder-gray-400"
                  placeholder="Enter your login ID"
                  autoFocus
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#3366FF] text-white font-semibold py-3.5 px-4 rounded-[3px] hover:bg-[#2952CC] focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {loading ? 'Processing...' : 'Continue'}
              </button>

              {googleSignInEnabled && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-gray-50 text-gray-500 font-medium">Or continue with</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-[3px] hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    <span>Sign in with Google</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleMicrosoftSignIn}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-[3px] hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                    </svg>
                    <span>Sign in with Microsoft</span>
                  </button>
                </>
              )}
            </form>
          ) : (
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              <div className="flex items-center gap-3 p-4 bg-blue-50 border-l-[3px] border-[#3366FF] rounded-[3px]">
                <div className="flex-shrink-0 w-10 h-10 bg-[#3366FF] rounded-[3px] flex items-center justify-center text-white font-semibold">
                  {loginId.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{loginId}</p>
                  <p className="text-xs text-gray-500">Agent Account</p>
                </div>
                <button
                  type="button"
                  onClick={handleBack}
                  className="text-[#3366FF] hover:text-[#2952CC] text-sm font-medium transition-colors"
                >
                  Change
                </button>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-[3px] focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:border-[#3366FF] transition-all text-gray-900 placeholder-gray-400"
                  placeholder="Enter your password"
                  autoFocus
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#3366FF] text-white font-semibold py-3.5 px-4 rounded-[3px] hover:bg-[#2952CC] focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>

              {googleSignInEnabled && (
                <>
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-gray-50 text-gray-500 font-medium">Or continue with</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-[3px] hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    <span>Sign in with Google</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleMicrosoftSignIn}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-3 px-4 rounded-[3px] hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                    </svg>
                    <span>Sign in with Microsoft</span>
                  </button>
                </>
              )}
            </form>
          )}

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-400 font-mono tracking-wide">
              {(() => {
                const { version, date } = getBuildInfo()
                return `BUILD v${version} — ${date}`
              })()}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
